import { useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import type { AboutContent } from '@/content/about';

interface RequestCallbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  copy: AboutContent['callback'];
  language: string;
}

const schema = z.object({
  name: z.string().trim().min(2).max(100),
  company: z.string().trim().max(120),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(5).max(40),
  profile: z.enum(['shipper', 'transporter']),
  message: z.string().trim().max(1000),
});

const DIAL_CODES = [
  { iso: 'FR', code: '+33', flag: '🇫🇷' },
  { iso: 'BE', code: '+32', flag: '🇧🇪' },
  { iso: 'LU', code: '+352', flag: '🇱🇺' },
  { iso: 'IT', code: '+39', flag: '🇮🇹' },
  { iso: 'ES', code: '+34', flag: '🇪🇸' },
  { iso: 'DE', code: '+49', flag: '🇩🇪' },
  { iso: 'NL', code: '+31', flag: '🇳🇱' },
  { iso: 'PT', code: '+351', flag: '🇵🇹' },
  { iso: 'GB', code: '+44', flag: '🇬🇧' },
  { iso: 'CH', code: '+41', flag: '🇨🇭' },
  { iso: 'AT', code: '+43', flag: '🇦🇹' },
  { iso: 'PL', code: '+48', flag: '🇵🇱' },
  { iso: 'RO', code: '+40', flag: '🇷🇴' },
  { iso: 'US', code: '+1', flag: '🇺🇸' },
];

const inputClass =
  'w-full px-4 py-3 rounded-lg border border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 outline-none transition-all duration-200 placeholder:text-[#87ADD4] text-sm text-[#2D3748] bg-white';
const labelClass = 'block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-2';


export const RequestCallbackDialog = ({
  open,
  onOpenChange,
  copy,
  language,
}: RequestCallbackDialogProps) => {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [profile, setProfile] = useState<'' | 'shipper' | 'transporter'>('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [dialCode, setDialCode] = useState('+33');

  const update = (key: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = schema.safeParse({ ...form, profile, phone: `${dialCode} ${form.phone}`.trim() });
    if (!parsed.success || !consent) {
      const fieldErrors = parsed.success ? {} : parsed.error.flatten().fieldErrors;
      setErrors(
        Object.fromEntries(Object.keys(fieldErrors).map((key) => [key, true])) as Record<string, boolean>,
      );
      if (!consent) toast.error(copy.consent);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('request-callback', {
        body: { ...parsed.data, language },
      });

      if (error) throw error;
      toast.success(copy.success);
      setForm({ name: '', company: '', email: '', phone: '', message: '' });
      setProfile('');
      setConsent(false);
      onOpenChange(false);
    } catch (error) {
      console.error('request-callback failed', error);
      toast.error(copy.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-medium text-[#37699E]">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5A6B7A]">{copy.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="cb-name">
                {copy.name}
              </label>
              <input
                id="cb-name"
                value={form.name}
                onChange={(e) => update('name')(e.target.value)}
                className={`${inputClass} ${errors.name ? 'border-[#FF7171] ring-2 ring-[#FF7171]/20' : ''}`}
                maxLength={100}
                required
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="cb-company">
                {copy.company}
              </label>
              <input
                id="cb-company"
                value={form.company}
                onChange={(e) => update('company')(e.target.value)}
                className={inputClass}
                maxLength={120}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="cb-email">
                {copy.email}
              </label>
              <input
                id="cb-email"
                type="email"
                value={form.email}
                onChange={(e) => update('email')(e.target.value)}
                className={`${inputClass} ${errors.email ? 'border-[#FF7171] ring-2 ring-[#FF7171]/20' : ''}`}
                maxLength={255}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} htmlFor="cb-profile">
                {copy.profile}
              </label>
              <select
                id="cb-profile"
                value={profile}
                onChange={(e) => setProfile(e.target.value as 'shipper' | 'transporter')}
                className={`${inputClass} ${errors.profile ? 'border-[#FF7171] ring-2 ring-[#FF7171]/20' : ''}`}
                required
              >
                <option value="" disabled>
                  {copy.profileSelect}
                </option>
                <option value="shipper">{copy.profileShipper}</option>
                <option value="transporter">{copy.profileTransporter}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} htmlFor="cb-phone">
                {copy.phone}
              </label>
              <div className="flex gap-2">
                <select
                  aria-label="Indicatif"
                  value={dialCode}
                  onChange={(e) => setDialCode(e.target.value)}
                  className={`${inputClass} w-[104px] shrink-0 px-2`}
                >
                  {DIAL_CODES.map((c) => (
                    <option key={c.iso} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  id="cb-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone')(e.target.value)}
                  className={`${inputClass} flex-1 min-w-0 ${errors.phone ? 'border-[#FF7171] ring-2 ring-[#FF7171]/20' : ''}`}
                  placeholder="6 12 34 56 78"
                  maxLength={25}
                  required
                />
              </div>
            </div>


          </div>

          <div>
            <label className={labelClass} htmlFor="cb-message">
              {copy.message}
            </label>
            <textarea
              id="cb-message"
              rows={3}
              value={form.message}
              onChange={(e) => update('message')(e.target.value)}
              className={`${inputClass} resize-none`}
              placeholder={copy.messagePlaceholder}
              maxLength={1000}
            />
          </div>

          <label className="flex items-start gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="w-4 h-4 mt-1 rounded border-[#DBE2EA] text-[#0880AE] focus:ring-[#0880AE] focus:ring-2"
            />
            <span className="text-sm text-[#2D3748]">
              {copy.consent}{' '}
              <Link
                to="/privacy-policy"
                className="text-[#0880AE] hover:text-[#076A8E] underline transition-colors"
              >
                {copy.privacyLink}
              </Link>
            </span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 bg-[#FFBF07] hover:bg-[#E5AB06] active:bg-[#CC9805] text-[#2D3748] font-semibold px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? copy.submitting : copy.submit}
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="bg-white border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
            >
              {copy.cancel}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestCallbackDialog;
